import React, { useState, useEffect } from 'react';
import { 
  MapPin, 
  Wifi, 
  RefreshCw, 
  TrendingUp, 
  AlertCircle,
  Server,
  Activity,
  Grid3x3,
  ChevronDown,
  ChevronUp,
  Droplets,
  Thermometer,
  Wind,
  Sun,
  Zap,
  Leaf,
  Cloud
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface SensorValue {
  value: number;
  unit: string;
  status?: string;
  timestamp?: string;
  quality?: number;
}

interface DeviceData {
  deviceId: string;
  timestamp: string;
  sensors: Record<string, SensorValue>;
  system?: {
    wifi_signal?: number;
    uptime?: number;
  };
  status?: string;
  location?: string;
  zone?: string;
  zoneType?: string;
  area?: string;
}

interface LocationData {
  id: string;
  name: string;
  location: string;
  zones: Array<{
    id: string;
    name: string;
    type: string;
    cropType?: string;
    area: number;
    soilType?: string;
    irrigationType?: string;
    devices: number;
    status?: string;
  }>;
  totalArea: number;
}

const SENSOR_ICONS: Record<string, any> = {
  soilMoisture: Droplets,
  temperature: Thermometer,
  humidity: Wind,
  light: Sun,
  soilPH: Activity,
  soilEC: Zap,
  leafWetness: Leaf,
  co2: Cloud,
  pressure: Activity,
  flowRate: Droplets
};

const ZONE_TYPE_LABELS: Record<string, string> = {
  citrus_orchard: 'Verger Agrumes',
  olive_grove: 'Oliveraie',
  greenhouse: 'Serre',
  palm_grove: 'Palmeraie',
  pivot_irrigation: 'Pivot Irrigation'
};

const DemoPage: React.FC = () => {
  const [locations, setLocations] = useState<LocationData[]>([]);
  const [devices, setDevices] = useState<DeviceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [expandedZones, setExpandedZones] = useState<Set<string>>(new Set());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    fetchDemoData();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchDemoData(false);
    }, 30000); // Refresh every 30 seconds to avoid rate limiting

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchDemoData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);

      // Faire les requêtes séquentiellement avec délai pour éviter rate limiting
      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/demo`;
      
      const locationsRes = await fetch(`${apiUrl}/locations`);
      
      // Délai de 500ms entre les requêtes
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const devicesRes = await fetch(`${apiUrl}/devices`);

      // Vérifier les codes de statut
      if (!locationsRes.ok || !devicesRes.ok) {
        console.error('API Error:', locationsRes.status, devicesRes.status);
        if (devicesRes.status === 429 || locationsRes.status === 429) {
          console.warn('Rate limited, retrying in 5 seconds...');
          setTimeout(() => fetchDemoData(false), 5000);
          return;
        }
        throw new Error('Erreur lors du chargement des données');
      }

      const locationsData = await locationsRes.json();
      const devicesData = await devicesRes.json();

      // S'assurer que les données sont des tableaux
      const validLocations = Array.isArray(locationsData) ? locationsData : [];
      const validDevices = Array.isArray(devicesData) ? devicesData : [];

      setLocations(validLocations);
      setDevices(validDevices);
      setLastUpdate(new Date());

      // Ne mettre à jour selectedLocation que si elle n'est pas déjà définie
      if (!selectedLocation && validLocations.length > 0) {
        setSelectedLocation(validLocations[0].id);
      }
    } catch (error) {
      console.error('Error fetching demo data:', error);
      toast.error('Erreur lors du chargement des données de démo');
      // Initialiser avec des tableaux vides en cas d'erreur
      if (devices.length === 0) setDevices([]);
      if (locations.length === 0) setLocations([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const toggleZone = (zoneId: string) => {
    const newExpanded = new Set(expandedZones);
    if (newExpanded.has(zoneId)) {
      newExpanded.delete(zoneId);
    } else {
      newExpanded.add(zoneId);
    }
    setExpandedZones(newExpanded);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'critical': return 'text-red-600 bg-red-100 dark:bg-red-900/30';
      case 'warning': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30';
      default: return 'text-green-600 bg-green-100 dark:bg-green-900/30';
    }
  };

  const getSensorIcon = (sensorName: string) => {
    const Icon = SENSOR_ICONS[sensorName] || Activity;
    return Icon;
  };

  const renderSensorCard = (sensorName: string, sensorData: SensorValue) => {
    const Icon = getSensorIcon(sensorName);
    const statusColor = getStatusColor(sensorData.status);

    return (
      <div key={sensorName} className={`p-4 rounded-xl ${statusColor} transition-all border-2 ${
        sensorData.status === 'critical' ? 'border-red-300 dark:border-red-700' :
        sensorData.status === 'warning' ? 'border-yellow-300 dark:border-yellow-700' :
        'border-green-300 dark:border-green-700'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Icon className="h-5 w-5 font-bold" />
            <span className="text-sm font-bold capitalize">
              {sensorName.replace(/([A-Z])/g, ' $1').trim()}
            </span>
          </div>
          {sensorData.quality && (
            <span className="text-xs font-semibold bg-white dark:bg-gray-900 px-2 py-1 rounded">Q: {sensorData.quality}%</span>
          )}
        </div>
        <div className="flex items-baseline space-x-2">
          <span className="text-3xl font-black">{sensorData.value}</span>
          <span className="text-lg font-bold opacity-90">{sensorData.unit}</span>
        </div>
      </div>
    );
  };

  const getDevicesByZone = (zoneId: string) => {
    return devices.filter(d => {
      const location = locations.find(l => l.id === selectedLocation);
      if (!location) return false;
      const zone = location.zones.find(z => z.id === zoneId);
      return d.zone === zone?.name;
    });
  };

  const getLocationStats = (locationId: string) => {
    // S'assurer que devices est un tableau
    if (!Array.isArray(devices)) {
      return { total: 0, online: 0, totalSensors: 0, avgWifi: '0' };
    }
    
    const locationDevices = devices.filter(d => {
      const location = locations.find(l => l.id === locationId);
      return d.location === location?.name;
    });

    const online = locationDevices.filter(d => d.status === 'online').length;
    const totalSensors = locationDevices.reduce((sum, d) => sum + Object.keys(d.sensors).length, 0);
    const avgWifi = locationDevices.reduce((sum, d) => sum + (d.system?.wifi_signal || -70), 0) / (locationDevices.length || 1);

    return {
      total: locationDevices.length,
      online,
      totalSensors,
      avgWifi: avgWifi.toFixed(0)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement de la démo...</p>
        </div>
      </div>
    );
  }

  const selectedLocationData = locations.find(l => l.id === selectedLocation);
  const stats = selectedLocation ? getLocationStats(selectedLocation) : null;

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 pb-20 lg:pb-6">
      {/* Demo Banner */}
      <div className="bg-gradient-to-br from-emerald-600 via-green-600 to-teal-600 rounded-xl shadow-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full -ml-24 -mb-24"></div>
        <div className="relative z-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2 flex items-center gap-3">
                <span className="text-5xl">🌾</span>
                <span>Dashboard IoT Agricole</span>
              </h1>
              <p className="text-emerald-50 text-lg">
                Surveillance en temps réel de 3 exploitations • 18 appareils actifs
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-5 py-2.5 rounded-lg font-semibold transition-all transform hover:scale-105 flex items-center gap-2 shadow-lg ${
                  autoRefresh 
                    ? 'bg-white text-emerald-600 hover:bg-emerald-50' 
                    : 'bg-emerald-500 text-white hover:bg-emerald-400 ring-2 ring-white ring-opacity-50'
                }`}
              >
                <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                <span>{autoRefresh ? 'Auto (30s)' : 'Manuel'}</span>
              </button>
              <button
                onClick={() => fetchDemoData(true)}
                className="px-5 py-2.5 bg-white text-emerald-600 rounded-lg hover:bg-emerald-50 transition-all transform hover:scale-105 flex items-center gap-2 font-semibold shadow-lg"
              >
                <RefreshCw className="h-4 w-4" />
                <span className="hidden sm:inline">Actualiser</span>
              </button>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-lg backdrop-blur-sm">
              <Activity className="h-4 w-4" />
              <span>Dernière mise à jour: {format(lastUpdate, 'HH:mm:ss', { locale: fr })}</span>
            </div>
            <div className="flex items-center gap-2 bg-white bg-opacity-20 px-4 py-2 rounded-lg backdrop-blur-sm">
              <TrendingUp className="h-4 w-4" />
              <span>Données en temps réel</span>
            </div>
          </div>
        </div>
      </div>

      {/* Locations Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <MapPin className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
          <span>Emplacements</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {locations.map(location => (
            <button
              key={location.id}
              onClick={() => setSelectedLocation(location.id)}
              className={`group relative overflow-hidden rounded-xl transition-all transform hover:scale-105 ${
                selectedLocation === location.id
                  ? 'bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-2xl scale-105 ring-4 ring-emerald-300 dark:ring-emerald-500'
                  : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 text-gray-700 dark:text-gray-300 hover:shadow-lg'
              }`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <MapPin className={`h-7 w-7 ${
                    selectedLocation === location.id
                      ? 'text-white'
                      : 'text-emerald-600 dark:text-emerald-400'
                  }`} />
                  {selectedLocation === location.id && (
                    <span className="px-3 py-1 bg-white bg-opacity-30 rounded-full text-xs font-semibold">
                      Actif
                    </span>
                  )}
                </div>
                <div className="text-left">
                  <div className="font-bold text-lg mb-1">{location.name}</div>
                  <div className={`text-sm mb-3 ${
                    selectedLocation === location.id
                      ? 'text-emerald-50'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    📍 {location.location}
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className={selectedLocation === location.id ? 'text-emerald-100' : 'text-gray-600 dark:text-gray-400'}>
                      {location.zones.length} zone{location.zones.length > 1 ? 's' : ''}
                    </span>
                    <span className={selectedLocation === location.id ? 'text-emerald-100' : 'text-gray-600 dark:text-gray-400'}>
                      {location.zones.reduce((sum, z) => sum + z.devices, 0)} appareil{location.zones.reduce((sum, z) => sum + z.devices, 0) > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
              <div className={`h-1 w-full transition-all ${
                selectedLocation === location.id
                  ? 'bg-emerald-400'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}></div>
            </button>
          ))}
        </div>
      </div>

      {/* Location Stats */}
      {stats && selectedLocationData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/40 dark:to-blue-800/40 rounded-xl shadow-lg p-5 border border-blue-200 dark:border-blue-700 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-blue-600 dark:bg-blue-500 rounded-xl shadow-md">
                <Server className="h-6 w-6 text-white" />
              </div>
              <div className="text-3xl font-black text-blue-600 dark:text-blue-300">
                {selectedLocationData.zones.length}
              </div>
            </div>
            <p className="text-sm font-semibold text-blue-700 dark:text-blue-200">Zones agricoles</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/40 dark:to-emerald-800/40 rounded-xl shadow-lg p-5 border border-emerald-200 dark:border-emerald-700 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-emerald-600 dark:bg-emerald-500 rounded-xl shadow-md">
                <Activity className="h-6 w-6 text-white" />
              </div>
              <div className="text-3xl font-black text-emerald-600 dark:text-emerald-300">
                {stats.total}
              </div>
            </div>
            <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-200">Appareils actifs</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/40 dark:to-purple-800/40 rounded-xl shadow-lg p-5 border border-purple-200 dark:border-purple-700 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-purple-600 dark:bg-purple-500 rounded-xl shadow-md">
                <Grid3x3 className="h-6 w-6 text-white" />
              </div>
              <div className="text-3xl font-black text-purple-600 dark:text-purple-300">
                {stats.totalSensors}
              </div>
            </div>
            <p className="text-sm font-semibold text-purple-700 dark:text-purple-200">Mesures totales</p>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/40 dark:to-amber-800/40 rounded-xl shadow-lg p-5 border border-amber-200 dark:border-amber-700 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="p-3 bg-amber-600 dark:bg-amber-500 rounded-xl shadow-md">
                <Wifi className="h-6 w-6 text-white" />
              </div>
              <div className="text-2xl font-black text-amber-600 dark:text-amber-300">
                {stats.avgWifi}
              </div>
            </div>
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-200">WiFi moyen (dBm)</p>
          </div>
        </div>
      )}

      {/* Zones and Devices */}
      {selectedLocationData && selectedLocationData.zones && (
        <div className="space-y-4">
          {selectedLocationData.zones.map(zone => {
            const zoneDevices = getDevicesByZone(zone.id);
            const isExpanded = expandedZones.has(zone.id);

            return (
              <div key={zone.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <button
                  onClick={() => toggleZone(zone.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <MapPin className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {zone.name}
                      </h3>
                      <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
                        <span>{ZONE_TYPE_LABELS[zone.type] || zone.type}</span>
                        <span>•</span>
                        <span>{zone.area}</span>
                        <span>•</span>
                        <span>{zoneDevices.length} appareil{zoneDevices.length > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>

                {isExpanded && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                    {zoneDevices.map(device => (
                      <div key={device.deviceId} className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700">
                        {/* Device Header */}
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Server className="h-5 w-5" />
                              <span className="font-bold">{device.deviceId}</span>
                            </div>
                            <div className="flex items-center space-x-3 text-sm">
                              {device.system?.wifi_signal && (
                                <span className="flex items-center space-x-1">
                                  <Wifi className="h-4 w-4" />
                                  <span>{device.system.wifi_signal} dBm</span>
                                </span>
                              )}
                              <span className={`px-2 py-1 rounded ${
                                device.status === 'online' 
                                  ? 'bg-green-500/20 text-green-100' 
                                  : 'bg-red-500/20 text-red-100'
                              }`}>
                                {device.status === 'online' ? 'En ligne' : 'Hors ligne'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Sensors Grid */}
                        <div className="p-5 bg-gray-50 dark:bg-gray-900/50">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {Object.entries(device.sensors).map(([sensorName, sensorData]) => 
                              renderSensorCard(sensorName, sensorData)
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5 shadow-sm">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5" />
          <div className="text-sm text-emerald-800 dark:text-emerald-200">
            <p className="font-bold mb-2 text-base">ℹ️ À propos des données</p>
            <p>
              Les valeurs affichées sont simulées en temps réel pour représenter des conditions agricoles réalistes. 
              Les mesures varient selon l'heure de la journée, la localisation géographique et les caractéristiques 
              spécifiques de chaque zone agricole.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
